import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { RiLightbulbLine, RiPrinterFill } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import ReactSelect from "react-select";
import { useReactToPrint } from "react-to-print";
import YouTube from "react-youtube";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import { adminModalContentProps, adminSelectStyles } from "../../../components/Models/admin-modal-theme";
import { brandPalette } from "../../../constants/brandPalette";
import { API } from "../../../constants";
import { getAllChaptersRequest } from "../../../modules/admin-modules/chapters/Actions";
import { getAllQuestionsBankRequest } from "../../../modules/admin-modules/questions-bank/Actions";

const chapterSelectBase = adminSelectStyles({ menuZIndex: 1600 });
const selectStyles = {
  ...chapterSelectBase,
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
  control: (styles, state) => ({
    ...chapterSelectBase.control(styles, state),
    borderRadius: "14px",
    minHeight: "44px",
    minWidth: "220px",
  }),
};

const hintFilterOptions = [
  { value: "all", label: "All questions" },
  { value: "no-hint", label: "Without hint only" },
];

const youtubeModalOpts = {
  height: 360,
  width: 640,
  playerVars: { autoplay: 0 },
};

function resolveAssetUrl(raw) {
  if (!raw || typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed || trimmed === "null") return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const base = String(API || "")
    .replace(/\/?api\/?$/i, "")
    .replace(/\/$/, "");
  const origin = base || (typeof window !== "undefined" ? window.location.origin : "");
  return `${origin}/${trimmed.replace(/^\//, "")}`;
}

function extractVideoId(url) {
  if (!url || typeof url !== "string") return null;
  const parts = url.split("/");
  return parts[parts.length - 1] || null;
}

function StatusBadge({ children, colorScheme = "green" }) {
  return (
    <Badge
      colorScheme={colorScheme}
      borderRadius="8px"
      px={2}
      py={1}
      fontSize="xs"
      fontWeight="700"
      textTransform="none"
    >
      {children}
    </Badge>
  );
}

function QuestionMeta({ question }) {
  return (
    <VStack align="flex-start" spacing={2} flexShrink={0} minW="100px" className="print-question-meta">
      {question?.book_marked == 1 && <StatusBadge colorScheme="yellow">Bookmarked</StatusBadge>}
      <StatusBadge colorScheme={question?.deleted_at != null ? "red" : "green"}>
        {question?.deleted_at != null ? "Suspended" : "Open"}
      </StatusBadge>
      {question?.model_answer_image && (
        <Badge className="print-hide-hints-trigger" colorScheme="orange" borderRadius="8px" px={2} py={1} fontSize="xs" fontWeight="700" textTransform="none">
          With hint
        </Badge>
      )}
    </VStack>
  );
}

function QuestionImage({ src, alt = "Question" }) {
  if (!src) return null;
  return (
    <Box
      w="100%"
      py={3}
      borderBottom="1px solid"
      borderColor="var(--border-subtle)"
      bg="var(--surface-muted)"
      borderRadius="12px"
      display="flex"
      alignItems="center"
      justifyContent="center"
      minH="140px"
      maxH="280px"
      className="print-question-image"
    >
      <Image src={src} maxH="260px" maxW="100%" objectFit="contain" alt={alt} />
    </Box>
  );
}

const OPTION_LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

function AnswerList({ answers }) {
  return (
    <VStack w="100%" alignItems="flex-start" spacing={2} px={{ base: 0, md: 16 }} className="print-answers-list">
      {answers?.map((answer, index) => {
        const isCorrect = answer?.right_answer == 1;
        const optionLabel = OPTION_LETTERS[index] ?? String(index + 1);
        return (
          <Flex
            key={index}
            align="flex-start"
            gap={3}
            w="100%"
            px={3}
            py={2}
            borderRadius="10px"
            fontWeight={isCorrect ? "700" : "500"}
            bg={isCorrect ? "rgba(72, 187, 120, 0.22)" : "transparent"}
            color="var(--text-primary)"
            border="1px solid"
            borderColor={isCorrect ? "rgba(72, 187, 120, 0.45)" : "var(--border-subtle)"}
            className={`print-answer${isCorrect ? " print-answer--correct" : ""}`}
          >
            <Text
              as="span"
              flexShrink={0}
              fontWeight="800"
              fontSize="sm"
              color={isCorrect ? "green.600" : "var(--text-muted)"}
              className="print-answer-label"
              minW="1.25rem"
            >
              {optionLabel}.
            </Text>
            <Box
              as="span"
              flex={1}
              textAlign="left"
              className="print-answer-text"
              dangerouslySetInnerHTML={{ __html: answer?.answer ?? "" }}
            />
          </Flex>
        );
      })}
    </VStack>
  );
}

function QuestionBlock({ question, index, onOpenHints }) {
  const imageSrc = resolveAssetUrl(question?.image);
  const hintImageSrc = resolveAssetUrl(question?.model_answer_image);
  const videoId = extractVideoId(question?.url);
  const hasHints = Boolean(hintImageSrc || videoId);

  return (
    <VStack
      w="100%"
      alignItems="flex-start"
      spacing={4}
      py={6}
      borderBottom="1px solid"
      borderColor="var(--border-subtle)"
      _last={{ borderBottom: "none" }}
      className="print-question-block"
    >
      <QuestionImage src={imageSrc} />

      <HStack w="100%" alignItems="flex-start" spacing={4} flexDir={{ base: "column", sm: "row" }} className="print-question-header">
        <Flex
          flexShrink={0}
          align="center"
          justify="center"
          minW="36px"
          h="36px"
          borderRadius="full"
          bg="rgba(91, 13, 22, 0.12)"
          color={brandPalette.primary}
          fontWeight="900"
          fontSize="sm"
          className="print-question-number"
        >
          {index + 1}
        </Flex>
        <VStack flex={1} alignItems="flex-start" spacing={2} w="100%">
          {question?.comprehension && (
            <Box
              w="100%"
              px={3}
              py={2}
              borderRadius="10px"
              bg="var(--surface-muted)"
              border="1px solid"
              borderColor="var(--border-subtle)"
              fontWeight="600"
              color="var(--text-muted)"
              fontSize="sm"
              textAlign="left"
              className="print-comprehension"
            >
              <Box as="span" fontWeight="800" color="var(--text-primary)">
                Comprehension:&nbsp;
              </Box>
              <Box as="span" dangerouslySetInnerHTML={{ __html: question.comprehension ?? "" }} />
            </Box>
          )}
          <Box fontWeight="700" color="var(--text-primary)" fontSize="md" textAlign="left" w="100%" className="print-question-text">
            <Box as="span" fontWeight="900" color={brandPalette.primary}>
              Question:&nbsp;
            </Box>
            <Box as="span" dangerouslySetInnerHTML={{ __html: question?.question ?? "" }} />
          </Box>
          {hasHints && (
            <Button
              type="button"
              leftIcon={<RiLightbulbLine />}
              mt={1}
              bg={brandPalette.secondary}
              color="white"
              borderRadius="14px"
              fontWeight="800"
              px={5}
              minH="44px"
              className="print-hide-hints-trigger"
              _hover={{ bg: brandPalette.primary }}
              onClick={() =>
                onOpenHints({
                  hintImageSrc,
                  videoId,
                  title: `Hints — Question ${index + 1}`,
                })
              }
            >
              Show hints
            </Button>
          )}
        </VStack>
        <QuestionMeta question={question} />
      </HStack>

      <AnswerList answers={question?.answers} />
    </VStack>
  );
}

const PrintableQuestions = () => {
  const dispatch = useDispatch();
  const [chapterId, setChapterId] = useState(null);
  const [withoutHintOnly, setWithoutHintOnly] = useState(false);
  const [hintModal, setHintModal] = useState({
    open: false,
    hintImageSrc: null,
    videoId: null,
    title: "",
  });

  const questionsBank = useSelector((state) => state.questionsBank.questionsBank);
  const isLoading = useSelector((state) => state.questionsBank.isLoading);
  const chapters = useSelector((state) => state.chapters.chapters);

  const chapterOptions = useMemo(
    () =>
      (chapters ?? []).map((chapter) => ({
        value: chapter.id,
        label: chapter.name,
      })),
    [chapters]
  );

  const selectedChapter = useMemo(
    () => chapterOptions.find((opt) => opt.value === chapterId) ?? null,
    [chapterOptions, chapterId]
  );

  const visibleQuestions = useMemo(() => {
    const list = Array.isArray(questionsBank) ? questionsBank : [];
    if (!withoutHintOnly) return list;
    return list.filter((q) => q?.model_answer_image == null);
  }, [questionsBank, withoutHintOnly]);

  useEffect(() => {
    if (!chapters?.length) dispatch(getAllChaptersRequest());
  }, [chapters, dispatch]);

  useEffect(() => {
    if (chapterId) dispatch(getAllQuestionsBankRequest(chapterId));
  }, [chapterId, dispatch]);

  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  return (
    <Layout>
      <Box
        dir="ltr"
        minH="400px"
        maxW="1440px"
        mx="auto"
        w="100%"
        px={{ base: 4, sm: 5, md: 8, lg: 10 }}
        pt={{ base: 6, md: 8 }}
        pb={{ base: 10, md: 12 }}
        textAlign="left"
      >
        <Flex
          w="100%"
          mb={6}
          justifyContent="space-between"
          alignItems={{ base: "stretch", md: "flex-start" }}
          gap={4}
          flexWrap="wrap"
        >
          <Box flex="1" minW="0">
            <Text fontSize={{ base: "18px", md: "24px" }} fontWeight="900" color="var(--text-primary)">
              Print Questions
            </Text>
            <Text mt={1} fontSize="sm" fontWeight="700" color="var(--text-muted)">
              Select a chapter to preview its question bank, then print the layout for offline use.
            </Text>
          </Box>

          <Flex
            align="flex-start"
            gap={3}
            flexWrap="wrap"
            justifyContent={{ base: "flex-start", md: "flex-end" }}
          >
            <Box minW={{ base: "100%", sm: "220px" }} maxW={{ base: "100%", md: "280px" }}>
              <ReactSelect
                options={chapterOptions}
                value={selectedChapter}
                onChange={(opt) => setChapterId(opt?.value ?? null)}
                styles={selectStyles}
                placeholder="Select chapter"
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
                isClearable
              />
            </Box>

            <Box minW={{ base: "100%", sm: "220px" }} maxW={{ base: "100%", md: "280px" }}>
              <ReactSelect
                options={hintFilterOptions}
                defaultValue={hintFilterOptions[0]}
                onChange={(opt) => setWithoutHintOnly(opt?.value === "no-hint")}
                styles={selectStyles}
                placeholder="Hint filter"
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
              />
            </Box>

            <Button
              leftIcon={<RiPrinterFill />}
              bg={brandPalette.secondary}
              color="white"
              borderRadius="14px"
              fontWeight="800"
              px={5}
              minH="44px"
              isDisabled={!chapterId || isLoading || visibleQuestions.length === 0}
              _hover={{ bg: brandPalette.primary }}
              _disabled={{ opacity: 0.5, cursor: "not-allowed" }}
              onClick={handlePrint}
            >
              Print
            </Button>

            <LogoutMenu />
          </Flex>
        </Flex>

        {!chapterId ? (
          <Text fontWeight="600" color="var(--text-muted)">
            Choose a chapter to load questions for printing.
          </Text>
        ) : (
          <Box
            ref={componentRef}
            dir="ltr"
            border="1px solid"
            borderColor="var(--border-subtle)"
            bg="var(--surface-raised)"
            borderRadius="18px"
            p={{ base: 4, md: 6 }}
            boxShadow="0 18px 55px rgba(0,0,0,0.12)"
            color="var(--text-primary)"
            className="printable-questions-preview"
            textAlign="left"
            sx={{
              "@media print": {
                WebkitPrintColorAdjust: "exact",
                printColorAdjust: "exact",
                bg: "white !important",
                color: "#1a1a1a !important",
                border: "none !important",
                boxShadow: "none !important",
                borderRadius: "0 !important",
                p: "12mm 10mm !important",
                fontSize: "11pt",
                lineHeight: "1.45",
                fontFamily: '"Segoe UI", system-ui, sans-serif',
                "& .print-hide-hints-trigger, & .print-question-meta": {
                  display: "none !important",
                },
                "& .print-document-header": {
                  borderBottom: "2px solid #5B0D16 !important",
                  mb: "6mm !important",
                  pb: "4mm !important",
                },
                "& .print-chapter-title": {
                  fontSize: "16pt !important",
                  fontWeight: "800 !important",
                  color: "#5B0D16 !important",
                },
                "& .print-document-subtitle": {
                  fontSize: "9pt !important",
                  color: "#666 !important",
                },
                "& .print-question-count": {
                  bg: "#f4efe9 !important",
                  color: "#5B0D16 !important",
                  border: "1px solid #d5cdc5 !important",
                  fontSize: "9pt !important",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                },
                "& .print-question-block": {
                  borderColor: "#d5cdc5 !important",
                  color: "#1a1a1a !important",
                  pageBreakInside: "avoid",
                  breakInside: "avoid",
                  py: "5mm !important",
                  mb: "2mm !important",
                },
                "& .print-question-number": {
                  bg: "#5B0D16 !important",
                  color: "white !important",
                  minW: "28px !important",
                  h: "28px !important",
                  fontSize: "10pt !important",
                },
                "& .print-comprehension": {
                  bg: "#f7f2ec !important",
                  borderColor: "#d5cdc5 !important",
                  color: "#444 !important",
                  fontSize: "10pt !important",
                  py: "2mm !important",
                  px: "3mm !important",
                },
                "& .print-question-text": {
                  fontSize: "11pt !important",
                  color: "#1a1a1a !important",
                  lineHeight: "1.5 !important",
                },
                "& .print-question-image": {
                  bg: "#fafafa !important",
                  border: "1px solid #e2e8f0 !important",
                  borderRadius: "4px !important",
                  minH: "auto !important",
                  maxH: "180px !important",
                  py: "2mm !important",
                  mb: "3mm !important",
                  "& img": {
                    maxH: "160px !important",
                  },
                },
                "& .print-answers-list": {
                  px: "0 !important",
                  mt: "3mm !important",
                  spacing: "2mm",
                },
                "& .print-answer": {
                  color: "#1a1a1a !important",
                  border: "1px solid #ccc !important",
                  borderRadius: "4px !important",
                  py: "2mm !important",
                  px: "3mm !important",
                  mb: "1.5mm !important",
                  bg: "white !important",
                  pageBreakInside: "avoid",
                },
                "& .print-answer--correct": {
                  bg: "#e8f5e9 !important",
                  borderColor: "#48bb78 !important",
                },
                "& .print-answer-label": {
                  color: "#5B0D16 !important",
                  fontWeight: "800 !important",
                },
                "& .print-answer--correct .print-answer-label": {
                  color: "#276749 !important",
                },
              },
            }}
          >
            {isLoading ? (
              <Flex align="center" justify="center" py={16} gap={3}>
                <Spinner color={brandPalette.primary} />
                <Text fontWeight="700" color="var(--text-primary)">
                  Loading questions…
                </Text>
              </Flex>
            ) : visibleQuestions.length === 0 ? (
              <Text fontWeight="600" color="var(--text-muted)" py={8}>
                No questions match the selected filters for this chapter.
              </Text>
            ) : (
              <>
                <Flex
                  justify="space-between"
                  align="center"
                  mb={4}
                  pb={4}
                  borderBottom="1px solid"
                  borderColor="var(--border-subtle)"
                  flexWrap="wrap"
                  gap={2}
                  className="print-document-header"
                >
                  <Box>
                    <Text fontSize="md" fontWeight="800" color="var(--text-primary)" className="print-chapter-title">
                      {selectedChapter?.label ?? "Chapter"}
                    </Text>
                    <Text fontSize="xs" fontWeight="600" color="var(--text-muted)" mt={1} className="print-document-subtitle">
                      Question bank — printable worksheet
                    </Text>
                  </Box>
                  <Badge
                    borderRadius="md"
                    px={2}
                    py={1}
                    bg="rgba(91, 13, 22, 0.18)"
                    color={brandPalette.secondary}
                    fontWeight="800"
                    className="print-question-count"
                  >
                    {visibleQuestions.length} question{visibleQuestions.length === 1 ? "" : "s"}
                  </Badge>
                </Flex>

                {visibleQuestions.map((question, idx) => (
                  <QuestionBlock
                    key={question?.id ?? idx}
                    question={question}
                    index={idx}
                    onOpenHints={(payload) => setHintModal({ open: true, ...payload })}
                  />
                ))}
              </>
            )}
          </Box>
        )}
      </Box>

      <Modal
        isOpen={hintModal.open}
        onClose={() => setHintModal((s) => ({ ...s, open: false }))}
        size="4xl"
        isCentered
      >
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent
          {...adminModalContentProps}
          dir="ltr"
          mx={4}
          border="1px solid"
          borderColor="var(--border-subtle)"
          color="var(--text-primary)"
        >
          <ModalHeader fontWeight="800" pb={2} color="var(--text-primary)" borderBottomWidth={0}>
            {hintModal.title || "Hints"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={8}>
            <VStack align="stretch" spacing={6}>
              {hintModal.hintImageSrc ? (
                <Box maxW="360px" w="100%" mx="auto">
                  <Image
                    src={hintModal.hintImageSrc}
                    alt="Model answer / hint"
                    maxW="100%"
                    maxH="240px"
                    height="auto"
                    borderRadius="lg"
                    border="1px solid"
                    borderColor="var(--border-subtle)"
                    objectFit="contain"
                    mx="auto"
                  />
                </Box>
              ) : null}
              {hintModal.videoId ? (
                <Box borderRadius="lg" overflow="hidden" maxW="640px" w="100%" mx="auto">
                  <YouTube
                    videoId={hintModal.videoId}
                    opts={youtubeModalOpts}
                    onReady={(event) => event.target.pauseVideo()}
                  />
                </Box>
              ) : null}
              {!hintModal.hintImageSrc && !hintModal.videoId ? (
                <Text color="var(--text-muted)" fontWeight="600">
                  No hint media for this question.
                </Text>
              ) : null}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Layout>
  );
};

export default PrintableQuestions;
